import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "quiseo_admin_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(!!token);

    const logout = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
        setUser(null);
    }, []);

    // verify token on mount / when it changes
    useEffect(() => {
        let cancelled = false;
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        axios
            .get(`${API}/admin/me`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                if (!cancelled) setUser(res.data);
            })
            .catch(() => {
                if (!cancelled) logout();
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token, logout]);

    const login = useCallback(async (email, password) => {
        const { data } = await axios.post(`${API}/admin/login`, {
            email,
            password,
        });
        localStorage.setItem(STORAGE_KEY, data.access_token);
        setToken(data.access_token);
        return data;
    }, []);

    const authHeaders = useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    const value = useMemo(
        () => ({ token, user, loading, login, logout, authHeaders }),
        [token, user, loading, login, logout, authHeaders]
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
